import { useState } from "react";
import { Box, Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Edit from "@mui/icons-material/Edit";
import { useMediaQuery } from "@mui/system";
import { settings } from "app";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import ManageNoteDialog from "components/Candidates/Show/components/ManageNoteDialog";
import ChangeCourseModal from "components/Users/Students/Modals/ChangeCourseModal";
import ChangeCurriculumModal from "components/Users/Students/Modals/ChangeCurriculumModal";
import ExternalDisciplinesModal from "components/Users/Students/Modals/ExternalDisciplinesModal";
import IndividualCurriculumButton from "components/Users/Students/IndividualCurriculum/IndividualCurriculumButton";
import ChangeFaculty from "components/Users/Students/Modals/ChangeFaculty";
import ChangeStatusModal from "components/Users/components/EditModals/ChangeStatusModal";
import ChangeUsesDormitory from "components/Users/Students/Modals/UsesDormitoryModal";
import { BooleanField, FunctionField, Labeled, ReferenceField, SimpleShowLayout, TextField } from "react-admin";
import SecuredComponent from "server/security/SecuredComponent";
import { checkPermission } from "server/security/utils";
import { CourseChoices } from "utils/choices";
import { EducationStatuses, STUDENTS } from "utils/constants";
import { administrationRequests } from "api/administrationApi";
import SportAthleteInfo from "./SportAthleteInfo";
import OtherUniversityInfo from "./OtherUniversityInfo";
import ChangeReceptionTypeModal from "../Modals/ChangeReceptionTypeModal";

const UniversityData = ({ record, fetchHistory }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const isAdmin = checkPermission(getPermissions());
  const [externalReloadKey, setExternalReloadKey] = useState(0);
  const isAvaliableToChangeFaculty =
    record?.specialityGroupId && record?.educationStatus === EducationStatuses.Active && record.course > 1;

  return (
    <SimpleShowLayout>
      <Grid
        spacing={2}
        container
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        {isAvaliableToChangeFaculty && (
          <SecuredComponent permission={permissions.canTransferStudentEducationFaculty}>
            <Grid
              item
              xs={12}
            >
              <ChangeFaculty record={record} />
            </Grid>
          </SecuredComponent>
        )}
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Учебен план">
            <TextField
              source="curriculumName"
              label="Учебен план"
            />
          </Labeled>
          <SecuredComponent permission={permissions.canManageStudentsIndividualCurriculums}>
            <div>
              <IndividualCurriculumButton record={record} />
            </div>
            <Box sx={{ mt: 1 }}>
              <ExternalDisciplinesModal
                record={record}
                onSaved={() => setExternalReloadKey(key => key + 1)}
              />
            </Box>
          </SecuredComponent>
          <SecuredComponent permission={permissions.canTransferStudentEducationCurriculum}>
            <Box sx={{ mt: 1 }}>
              <ChangeCurriculumModal record={record} />
            </Box>
          </SecuredComponent>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Специалност">
            <ReferenceField
              source="specialityId"
              reference={administrationRequests.getSpecialitiesSelect()}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Език на обучение">
            <ReferenceField
              source="instructionLanguageId"
              reference={administrationRequests.getInstructionLanguagesSelect()}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Тип на обучение">
            <ReferenceField
              source="studyTypeId"
              reference={administrationRequests.getStudyTypesSelect()}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Тип прием">
            <ReferenceField
              source="receptionTypeId"
              reference={administrationRequests.getReceptionTypesSelect()}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
          <div>
            <ChangeReceptionTypeModal record={record} />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Курс">
            <FunctionField render={record => CourseChoices.find(option => option.id === record.course)?.name} />
          </Labeled>
          {isAdmin && (
            <div>
              <ChangeCourseModal record={record} />
            </div>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Статус">
            <TextField
              source="educationStatus"
              emptyText="-"
            />
          </Labeled>

          <SecuredComponent permission={permissions.canChangeStudentEducationStatus}>
            <div>
              <ChangeStatusModal
                record={record}
                fetchHistory={fetchHistory}
              />
            </div>
          </SecuredComponent>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Статус причина">
            <TextField
              source="educationStatusReason"
              emptyText="-"
            />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Учебна група">
            <FunctionField render={record => (!record?.studyGroup ? "-" : record?.studyGroup)} />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Поток">
            <FunctionField render={record => (!record?.studyStream ? "-" : record?.studyStream)} />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Факултетен номер">
            <TextField
              source="facultyNumber"
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="ЕАН">
            <TextField
              source="ean"
              emptyText="-"
            />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Ползва общежитие">
            <BooleanField source="usesDormitory" />
          </Labeled>
          <SecuredComponent permission={permissions.canMarkStudentDormitoryUsage}>
            <div>
              <ChangeUsesDormitory record={record} />
            </div>
          </SecuredComponent>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Бележка">
            <TextField
              source="note"
              emptyText="-"
              sx={{ maxHeight: "200px", overflowY: "auto" }}
            />
          </Labeled>
          <SecuredComponent permission={permissions.canUpdateStudentEducationNote}>
            <div>
              <ManageNoteDialog
                startIcon={!record?.note ? <AddIcon /> : <Edit />}
                source="note"
                label={!record?.note ? "Добави бележка" : "Редактирай бележка"}
                resource={`${STUDENTS}/${record?.id}/note`}
              />
            </div>
          </SecuredComponent>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Основание за прием">
            <ReferenceField
              source="admissionReasonId"
              reference={administrationRequests.getAdmissionReasonsSelect()}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <SecuredComponent permission={permissions.canPreviewStudentEducationInfo}>
          <Grid
            item
            xs={12}
            md={4}
          >
            <SportAthleteInfo studentId={record?.studentId} />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ mt: 2 }}
          >
            <OtherUniversityInfo
              studentEducationId={record?.id}
              reloadKey={externalReloadKey}
            />
          </Grid>
        </SecuredComponent>
      </Grid>
    </SimpleShowLayout>
  );
};

export default UniversityData;
